import React from "react";
import styled from "styled-components";
import { tokens } from "../../theme/tokens";

// Tabs — branded, controlled tab strip (visual). Replaces raw .nav/.tab-content.
//
//   <Tabs
//      value={tab}
//      onChange={setTab}
//      tabs={[{key:"details", label:"Details"}, {key:"history", label:"History", count:3}]}
//   />
//   { tab === "details" && <DetailsPanel/> }

const { colors, radii, spacing, typography, motion } = tokens;

const Strip = styled.div`
    display: flex;
    align-items: center;
    gap: ${spacing[1]};
    border-bottom: 1px solid ${colors.divider};
    margin-bottom: ${spacing[5]};
    overflow-x: auto;
    &::-webkit-scrollbar { height: 0; }
`;

const Tab = styled.button`
    position: relative;
    appearance: none;
    border: 0;
    background: transparent;
    padding: ${spacing[3]} ${spacing[3]} 14px;
    font-family: ${typography.fonts.body};
    font-size: ${typography.size.base};
    font-weight: ${(p) => (p.$active ? typography.weight.bold : typography.weight.medium)};
    color: ${(p) => (p.$active ? colors.primary : colors.muted)};
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: color ${motion.fast} ${motion.ease};

    &:hover { color: ${(p) => (p.$active ? colors.primary : colors.heading)}; }

    &::after {
        content: "";
        position: absolute;
        left: 8px;
        right: 8px;
        bottom: -1px;
        height: 3px;
        border-radius: 3px 3px 0 0;
        background: ${(p) => (p.$active ? colors.primary : "transparent")};
        transition: background ${motion.fast} ${motion.ease};
    }
`;

const Count = styled.span`
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: ${radii.pill};
    background: ${(p) => (p.$active ? colors.rose : colors.surfaceSunken)};
    color: ${(p) => (p.$active ? colors.primary : colors.muted)};
    font-size: ${typography.size.xs};
    font-weight: ${typography.weight.bold};
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

export default function Tabs({ tabs = [], value, onChange, ...rest }) {
    return (
        <Strip role="tablist" {...rest}>
            {tabs.map((t) => {
                const active = t.key === value;
                return (
                    <Tab
                        key={t.key}
                        role="tab"
                        aria-selected={active}
                        $active={active}
                        onClick={() => onChange && onChange(t.key)}
                        type="button"
                    >
                        {t.label}
                        {t.count != null && <Count $active={active}>{t.count}</Count>}
                    </Tab>
                );
            })}
        </Strip>
    );
}
